import { apiJsonRequest } from './apiClient'

export type CatalogMasterStatus = 'active' | 'inactive'

export type CatalogMasterRecord = {
  id: string
  display_label: string
  value_key: string
  status: CatalogMasterStatus
  sort_order?: number
  created_at?: string
  updated_at?: string
}

export type CatalogMasterPayload = {
  display_label: string
  value_key: string
  status: CatalogMasterStatus
}

export type CatalogMasterResource =
  | 'payors'
  | 'plans'
  | 'product-services'
  | 'cpt-categories'
  | 'authorization-statuses'
  | 'contract-types'
  | 'billing-states'
  | 'submission-methods'
  | 'plan-types'
  | 'networks'
  | 'timely-filing-limits'
  | 'corrected-claim-deadlines'
  | 'coordination-of-benefits'
  | 'assignment-of-benefits'
  | 'claim-frequency-types'
  | 'rendering-provider-required'
  | 'modifiers'
  | 'fee-basis'
  | 'rental-cap-periods'
  | 'cap-resets-on'
  | 'equipment-categories'
  | 'transfer-triggers'
  | 'ownership-transfer-modifiers'
  | 'documentation-required'
  | 'pa-submission-methods'
  | 'standard-turnarounds'

const RESOURCE_LABELS: Record<CatalogMasterResource, string> = {
  payors: 'Payor master',
  plans: 'Plan master',
  'product-services': 'Product service master',
  'cpt-categories': 'CPT category master',
  'authorization-statuses': 'Authorization status master',
  'contract-types': 'Contract type master',
  'billing-states': 'Billing state master',
  'submission-methods': 'Submission method master',
  'plan-types': 'Plan type master',
  networks: 'Network master',
  'timely-filing-limits': 'Timely filing limit master',
  'corrected-claim-deadlines': 'Corrected claim deadline master',
  'coordination-of-benefits': 'Coordination of benefits master',
  'assignment-of-benefits': 'Assignment of benefits master',
  'claim-frequency-types': 'Claim frequency type master',
  'rendering-provider-required': 'Rendering provider required master',
  modifiers: 'Modifier master',
  'fee-basis': 'Fee basis master',
  'rental-cap-periods': 'Rental cap period master',
  'cap-resets-on': 'Cap resets on master',
  'equipment-categories': 'Equipment category master',
  'transfer-triggers': 'Transfer trigger master',
  'ownership-transfer-modifiers': 'Ownership transfer modifier master',
  'documentation-required': 'Documentation required master',
  'pa-submission-methods': 'PA submission method master',
  'standard-turnarounds': 'Standard turnaround master',
}

type ApiRow = Record<string, unknown>

type ListResponse = {
  data?: unknown
  items?: unknown
}

function asRecord(value: unknown): ApiRow | null {
  return value && typeof value === 'object' ? (value as ApiRow) : null
}

function readString(row: ApiRow, ...keys: string[]): string {
  for (const key of keys) {
    const value = row[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return ''
}

function resourceLabel(resource: CatalogMasterResource): string {
  return RESOURCE_LABELS[resource]
}

export function normalizeCatalogMaster(value: unknown): CatalogMasterRecord | null {
  const row = asRecord(value)
  if (!row) return null

  const id = readString(row, 'id')
  const display_label = readString(row, 'display_label', 'displayLabel', 'label', 'name')
  const value_key = readString(row, 'value_key', 'valueKey', 'value', 'code', 'slug')
  const statusRaw = readString(row, 'status').toLowerCase()
  const status: CatalogMasterStatus = statusRaw === 'inactive' ? 'inactive' : 'active'

  if (!id || !display_label || !value_key) return null

  const sortOrder =
    typeof row.sort_order === 'number'
      ? row.sort_order
      : typeof row.sortOrder === 'number'
        ? row.sortOrder
        : undefined

  return {
    id,
    display_label,
    value_key,
    status,
    sort_order: sortOrder,
    created_at: readString(row, 'created_at', 'createdAt') || undefined,
    updated_at: readString(row, 'updated_at', 'updatedAt') || undefined,
  }
}

function extractListItems(value: unknown): unknown[] {
  if (Array.isArray(value)) return value
  const record = asRecord(value)
  if (!record) return []
  if (Array.isArray(record.items)) return record.items
  if (Array.isArray(record.payors)) return record.payors
  if (Array.isArray(record.plans)) return record.plans
  if (Array.isArray(record.product_services)) return record.product_services
  if (Array.isArray(record.productServices)) return record.productServices
  if (Array.isArray(record.cpt_categories)) return record.cpt_categories
  if (Array.isArray(record.cptCategories)) return record.cptCategories
  if (Array.isArray(record.authorization_statuses)) return record.authorization_statuses
  if (Array.isArray(record.authorizationStatuses)) return record.authorizationStatuses
  if (Array.isArray(record.contract_types)) return record.contract_types
  if (Array.isArray(record.contractTypes)) return record.contractTypes
  if (Array.isArray(record.billing_states)) return record.billing_states
  if (Array.isArray(record.billingStates)) return record.billingStates
  if (Array.isArray(record.submission_methods)) return record.submission_methods
  if (Array.isArray(record.submissionMethods)) return record.submissionMethods
  if (Array.isArray(record.plan_types)) return record.plan_types
  if (Array.isArray(record.planTypes)) return record.planTypes
  if (Array.isArray(record.networks)) return record.networks
  if (Array.isArray(record.timely_filing_limits)) return record.timely_filing_limits
  if (Array.isArray(record.timelyFilingLimits)) return record.timelyFilingLimits
  if (Array.isArray(record.corrected_claim_deadlines)) return record.corrected_claim_deadlines
  if (Array.isArray(record.correctedClaimDeadlines)) return record.correctedClaimDeadlines
  if (Array.isArray(record.coordination_of_benefits)) return record.coordination_of_benefits
  if (Array.isArray(record.coordinationOfBenefits)) return record.coordinationOfBenefits
  if (Array.isArray(record.assignment_of_benefits)) return record.assignment_of_benefits
  if (Array.isArray(record.assignmentOfBenefits)) return record.assignmentOfBenefits
  if (Array.isArray(record.claim_frequency_types)) return record.claim_frequency_types
  if (Array.isArray(record.claimFrequencyTypes)) return record.claimFrequencyTypes
  if (Array.isArray(record.rendering_provider_required)) return record.rendering_provider_required
  if (Array.isArray(record.renderingProviderRequired)) return record.renderingProviderRequired
  if (Array.isArray(record.modifiers)) return record.modifiers
  if (Array.isArray(record.fee_basis)) return record.fee_basis
  if (Array.isArray(record.feeBasis)) return record.feeBasis
  if (Array.isArray(record.rental_cap_periods)) return record.rental_cap_periods
  if (Array.isArray(record.rentalCapPeriods)) return record.rentalCapPeriods
  if (Array.isArray(record.cap_resets_on)) return record.cap_resets_on
  if (Array.isArray(record.capResetsOn)) return record.capResetsOn
  if (Array.isArray(record.equipment_categories)) return record.equipment_categories
  if (Array.isArray(record.equipmentCategories)) return record.equipmentCategories
  if (Array.isArray(record.transfer_triggers)) return record.transfer_triggers
  if (Array.isArray(record.transferTriggers)) return record.transferTriggers
  if (Array.isArray(record.ownership_transfer_modifiers)) return record.ownership_transfer_modifiers
  if (Array.isArray(record.ownershipTransferModifiers)) return record.ownershipTransferModifiers
  if (Array.isArray(record.documentation_required)) return record.documentation_required
  if (Array.isArray(record.documentationRequired)) return record.documentationRequired
  if (Array.isArray(record.pa_submission_methods)) return record.pa_submission_methods
  if (Array.isArray(record.paSubmissionMethods)) return record.paSubmissionMethods
  if (Array.isArray(record.standard_turnarounds)) return record.standard_turnarounds
  if (Array.isArray(record.standardTurnarounds)) return record.standardTurnarounds
  return []
}

function normalizeList(
  body: ListResponse | CatalogMasterRecord[] | null | undefined
): CatalogMasterRecord[] {
  if (!body) return []
  if (Array.isArray(body)) {
    return body.map(normalizeCatalogMaster).filter(Boolean) as CatalogMasterRecord[]
  }

  const topLevelItems = extractListItems(body)
  if (topLevelItems.length > 0) {
    return topLevelItems.map(normalizeCatalogMaster).filter(Boolean) as CatalogMasterRecord[]
  }

  const nestedItems = extractListItems(body.data)
  if (nestedItems.length > 0) {
    return nestedItems.map(normalizeCatalogMaster).filter(Boolean) as CatalogMasterRecord[]
  }

  return []
}

function normalizeSingle(body: unknown, resource: CatalogMasterResource): CatalogMasterRecord {
  const record = asRecord(body)
  if (!record) {
    throw new Error(`${resourceLabel(resource)} response format is invalid.`)
  }

  if (record.data !== undefined) {
    const dataRecord = asRecord(record.data)
    if (dataRecord && dataRecord.item !== undefined) {
      const nested = normalizeCatalogMaster(dataRecord.item)
      if (nested) return nested
    }
    const nested = normalizeCatalogMaster(record.data)
    if (nested) return nested
  }

  if (record.item !== undefined) {
    const nested = normalizeCatalogMaster(record.item)
    if (nested) return nested
  }

  const direct = normalizeCatalogMaster(record)
  if (!direct) {
    throw new Error(`${resourceLabel(resource)} response format is invalid.`)
  }
  return direct
}

export function catalogMasterPayloadFromInput(input: {
  label: string
  value: string
  isActive?: boolean
}): CatalogMasterPayload {
  return {
    display_label: input.label.trim(),
    value_key: input.value.trim(),
    status: input.isActive === false ? 'inactive' : 'active',
  }
}

function resourcePath(resource: CatalogMasterResource): string {
  return `/masters/${resource}`
}

export async function listCatalogMasters(
  resource: CatalogMasterResource,
  q?: string
): Promise<CatalogMasterRecord[]> {
  const params = new URLSearchParams()
  if (q?.trim()) params.set('q', q.trim())
  const query = params.toString()
  const path = query ? `${resourcePath(resource)}?${query}` : resourcePath(resource)
  const response = await apiJsonRequest<ListResponse | CatalogMasterRecord[]>(path, { method: 'GET' })
  return normalizeList(response)
}

export async function getCatalogMaster(
  resource: CatalogMasterResource,
  masterId: string
): Promise<CatalogMasterRecord> {
  const response = await apiJsonRequest<unknown>(
    `${resourcePath(resource)}/${encodeURIComponent(masterId)}`,
    { method: 'GET' }
  )
  return normalizeSingle(response, resource)
}

export async function createCatalogMaster(
  resource: CatalogMasterResource,
  payload: CatalogMasterPayload
): Promise<CatalogMasterRecord> {
  const response = await apiJsonRequest<unknown>(resourcePath(resource), {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return normalizeSingle(response, resource)
}

export async function updateCatalogMaster(
  resource: CatalogMasterResource,
  masterId: string,
  payload: CatalogMasterPayload
): Promise<CatalogMasterRecord> {
  const response = await apiJsonRequest<unknown>(
    `${resourcePath(resource)}/${encodeURIComponent(masterId)}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    }
  )
  return normalizeSingle(response, resource)
}

export async function deleteCatalogMaster(
  resource: CatalogMasterResource,
  masterId: string
): Promise<void> {
  await apiJsonRequest(`${resourcePath(resource)}/${encodeURIComponent(masterId)}`, {
    method: 'DELETE',
  })
}
